/**
 * iter-154r — Persistent billing-status banner.
 *
 * Reads `user.billing_status` from AuthContext and renders an inline
 * warning when the workspace is past_due or paused. Provides a
 * one-click button that opens the Stripe customer portal so owners
 * can fix the payment method, view invoices, or reactivate.
 *
 * Hidden when billing_status is "active" or "trialing". For
 * "canceled" the user is auto-redirected to /billing/reactivate by
 * the axios 402 interceptor in AuthContext, so we don't render
 * anything for that state here.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, ExternalLink, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/lib/permissions';
import { apiClient } from '@/api/client';
import { extractMessage } from '@/api/errors';

const STATUS_COPY = {
  past_due: {
    label: 'Payment failed',
    detail: "We couldn't process your latest invoice. Update your payment method to avoid an interruption.",
    tone: 'amber',
  },
  paused: {
    label: 'Subscription paused',
    detail: 'Your workspace is currently paused. Resume billing to unlock changes.',
    tone: 'amber',
  },
  pending: {
    label: 'Activation pending',
    detail: 'Your subscription is still being set up. Once Stripe confirms the first payment, full access kicks in.',
    tone: 'slate',
  },
};

const TONE_STYLES = {
  amber: 'bg-[#3A2A12] border-amber-700/40 text-amber-100',
  slate: 'bg-[#1F1F22] border-border text-muted-foreground',
};

export default function BillingStatusBanner() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const [loading, setLoading] = useState(false);
  const status = user?.billing_status;
  const copy = STATUS_COPY[status];
  if (!copy) return null;
  // Billing is gated on the `manage_billing` permission (audit I3); owners
  // pass automatically via can().
  const canManage = can('manage_billing');

  // For past_due, show the live grace countdown the backend computed
  // (3-day window from the first failed payment) so the owner knows
  // exactly how long until the workspace locks.
  let detail = copy.detail;
  const daysLeft = user?.billing_grace_days_left;
  if (status === 'past_due' && typeof daysLeft === 'number') {
    detail = daysLeft > 0
      ? `We couldn't process your latest invoice. You have ${daysLeft} day${daysLeft === 1 ? '' : 's'} to update your payment method before your workspace is locked.`
      : "We couldn't process your latest invoice. Your grace period has ended — update your payment method to restore access.";
  }

  const openPortal = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.post(
        '/billing/portal-session',
        { return_url: window.location.href },
      );
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('No portal URL returned');
        setLoading(false);
      }
    } catch (e) {
      toast.error(extractMessage(e, 'Could not open the billing portal'));
      setLoading(false);
    }
  };

  return (
    <div
      data-testid={`billing-banner-${status}`}
      className={`flex items-center gap-3 px-4 py-2 border-b text-xs ${TONE_STYLES[copy.tone]}`}
    >
      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <strong className="font-semibold">{copy.label}.</strong>{' '}
        <span className="opacity-90">{detail}</span>
      </div>
      {canManage && (
        <button
          onClick={openPortal}
          disabled={loading}
          data-testid="billing-banner-portal-btn"
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded text-[11px] font-medium bg-white/10 hover:bg-white/20 border border-white/20 disabled:opacity-50"
        >
          {loading ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <ExternalLink className="w-3 h-3" />
          )}
          Manage billing
        </button>
      )}
    </div>
  );
}
